import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { organization, content, branding } from "../../config";

export default function DonatePublic() {
    const [donationSections, setDonationSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const sectionRefs = useRef({});
    const [hoveredIndex, setHoveredIndex] = useState(null);

    useEffect(() => {
        document.title = `Donate - ${organization.name}`;
    }, []);

    useEffect(() => {
        const fetchDonationSections = async () => {
            try {
                const res = await axios.get("/api/public/donation");
                setDonationSections(res.data.content || []);
            } catch (error) {
                console.error("Error fetching donation sections:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDonationSections();
    }, []);

    const paymentMethods = {
        "visa": { label: "Visa", icon: "fab fa-cc-visa" },
        "mastercard": { label: "Mastercard", icon: "fab fa-cc-mastercard" },
        "amex": { label: "American Express", icon: "fab fa-cc-amex" },
        "discover": { label: "Discover", icon: "fab fa-cc-discover" },
        "paypal": { label: "PayPal", icon: "fab fa-cc-paypal" }
    };

    const scrollToSection = (index) => {
        sectionRefs.current[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    };

    if (loading) {
        return (
            <div className="container my-5 text-center">
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {/* Hero Section */}
            <section
                className="text-center py-5 rounded-4"
                style={{
                    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor || '#004466'} 100%)`,
                    color: "white",
                }}
            >
                <div className="container">
                    <p
                        className="text-uppercase mb-2"
                        style={{
                            letterSpacing: "2px",
                            fontSize: "0.9rem",
                            fontWeight: "500",
                            opacity: 0.9
                        }}
                    >
                        Let's Make A Difference
                    </p>
                    <h1
                        className="display-4 fw-bold mb-3"
                        style={{ fontWeight: "700" }}
                    >
                        Donate Today
                    </h1>
                    <p
                        className="lead mb-4"
                        style={{
                            maxWidth: "600px",
                            margin: "0 auto",
                            fontSize: "1.1rem",
                            opacity: 0.95
                        }}
                    >
                        Your help makes all the difference. Scroll down to learn how you contribute to relief efforts in {organization.location || "Talbot County"}.
                    </p>
                </div>
            </section>

            {/* Navigation Grid */}
            {donationSections.length > 0 && (
                <section className="container-fluid my-5">
                    <div className={`row g-3 row-cols-1 row-cols-md-1 row-cols-lg-${Math.min(donationSections.length, 6)}`}>
                        {donationSections.map((section, index) => (
                            <div key={index} className="col">
                                <button
                                    onClick={() => scrollToSection(index)}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(null)}
                                    className="w-100 h-100 btn btn-light text-center p-4"
                                    style={{
                                        border: "none",
                                        boxShadow: hoveredIndex === index 
                                            ? "0 8px 20px rgba(0, 0, 0, 0.3)" 
                                            : "0 2px 8px rgba(0, 0, 0, 0.2)",
                                        borderRadius: "12px",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer",
                                        backgroundColor: "white",
                                        transform: hoveredIndex === index ? "translateY(-2px)" : "translateY(0)",
                                    }}
                                >
                                    {section.icon && (
                                        <i
                                            className={section.icon}
                                            style={{
                                                fontSize: "2.5rem",
                                                color: branding.primaryColor,
                                                marginBottom: "10px",
                                                display: "block",
                                                transition: "all 0.3s ease",
                                                transform: hoveredIndex === index ? "scale(1.1)" : "scale(1)",
                                            }}
                                        ></i>
                                    )}
                                    <div style={{ 
                                        fontWeight: "600", 
                                        color: hoveredIndex === index ? branding.primaryColor : "#333", 
                                        fontSize: "1rem",
                                        transition: "color 0.3s ease"
                                    }}>
                                        {section.label}
                                    </div>
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

            )}

            {/* Donation Sections */}
            <div className="container my-5">
                {donationSections.length === 0 ? (
                    <div className="alert alert-info text-center">
                        No donation options available at this time.
                    </div>
                ) : (
                    donationSections.map((section, index) => (
                        <section
                            key={index}
                            ref={(el) => (sectionRefs.current[index] = el)}
                            className="mb-5 pb-5"
                            style={{
                                borderBottom: index < donationSections.length - 1 ? "1px solid #e0e0e0" : "none",
                            }}
                        >
                            <div className="row align-items-start">
                                {/* Left Column - Main Content */}
                                <div className="col-lg-7">
                                    {/* Section Header */}
                                    <div className="mb-4">
                                        {section.label && (
                                            <p
                                                className="text-uppercase mb-2"
                                                style={{
                                                    color: branding.primaryColor,
                                                    letterSpacing: "1.5px",
                                                    fontSize: "0.85rem",
                                                    fontWeight: "600"
                                                }}
                                            >
                                                {section.label}
                                            </p>
                                        )}
                                        {section.header && (
                                            <h2
                                                className="mb-3"
                                                style={{
                                                    fontWeight: "700",
                                                    color: "#333",
                                                    fontSize: "2rem"
                                                }}
                                            >
                                                {section.header}
                                            </h2>
                                        )}
                                        {section.description && (
                                            <p
                                                className="lead text-muted"
                                                style={{ fontSize: "1.1rem", lineHeight: "1.6", whiteSpace: "pre-wrap" }}
                                            >
                                                {section.description}
                                            </p>
                                        )}
                                    </div>

                                    {/* Optional Image */}
                                    {section.image && (
                                        <div className="mb-4">
                                            <img 
                                                src={section.image} 
                                                alt={section.header || section.label || "Donation"} 
                                                loading="lazy"
                                                style={{
                                                    width: "100%",
                                                    maxHeight: "300px",
                                                    objectFit: "cover",
                                                    borderRadius: "12px",
                                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)"
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Button Section */}
                                    {section.enableButton && (
                                        <div
                                            className="p-4 mb-4"
                                            style={{
                                                backgroundColor: "white",
                                                borderRadius: "12px",
                                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                            }}
                                        >
                                            {section.buttonLabel && section.buttonLink && (
                                                <a
                                                    href={section.buttonLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`btn btn-${section.buttonColor || 'primary'} w-100 mb-3`}
                                                    style={{
                                                        padding: "12px 24px",
                                                        fontSize: "1rem",
                                                        fontWeight: "600",
                                                        borderRadius: "8px",
                                                    }}
                                                >
                                                    {section.buttonIcon && (
                                                        <i className={`${section.buttonIcon} ms-2`}> </i>
                                                    )}
                                                    {section.buttonLabel}
                                                </a>
                                            )}

                                            {/* Payment Methods */}
                                            {section.acceptedPayments && section.acceptedPayments.length > 0 && (
                                                <div className="text-center mb-2">
                                                    <ul
                                                        className="list-inline mb-0"
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            gap: "10px",
                                                            flexWrap: "wrap"
                                                        }}
                                                    >
                                                        {section.acceptedPayments.map((methodId) => {
                                                            const method = paymentMethods[methodId];
                                                            return method ? (
                                                                <li key={methodId} className="list-inline-item">
                                                                    <i
                                                                        className={method.icon}
                                                                        style={{
                                                                            fontSize: "1.5rem",
                                                                            color: "#666"
                                                                        }}
                                                                        title={method.label}
                                                                    ></i>
                                                                </li>
                                                            ) : null;
                                                        })}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Fine Print */}
                                            {section.finePrint && (
                                                <p
                                                    className="text-muted small text-center mb-0"
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        lineHeight: "1.4"
                                                    }}
                                                >
                                                    {section.finePrint}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Right Column - Additional Section */}
                                <div className="col-lg-5">
                                    {/* Additional Section (FAQ or General) */}
                                    {section.enableAdditionalSection && (
                                        <div
                                            className="p-4"
                                            style={{
                                                // backgroundColor: "white",
                                                // borderRadius: "12px",
                                                // boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                            }}
                                        >
                                            {section.sectionType === "faq" ? (
                                                <div>
                                                    <h5 className="mb-3" style={{ fontWeight: "600", fontSize: "1.1rem" }}>
                                                        Frequently Asked Questions
                                                    </h5>
                                                    <div className="accordion" id={`faq-accordion-${index}`}>
                                                        {(section.faqItems || []).map((faq, faqIndex) => (
                                                            <div className="accordion-item mb-2" key={faqIndex} style={{ border: "none", borderRadius: "6px", boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)" }}>
                                                                <h2 className="accordion-header" id={`faq-heading-${index}-${faqIndex}`}>
                                                                    <button
                                                                        className="accordion-button collapsed"
                                                                        type="button"
                                                                        data-bs-toggle="collapse"
                                                                        data-bs-target={`#faq-collapse-${index}-${faqIndex}`}
                                                                        aria-expanded="false"
                                                                        aria-controls={`faq-collapse-${index}-${faqIndex}`}
                                                                        style={{
                                                                            fontWeight: "600",
                                                                            fontSize: "0.9rem",
                                                                            backgroundColor: "white",
                                                                            padding: "12px 16px"
                                                                        }}
                                                                    >
                                                                        {faq.question}
                                                                    </button>
                                                                </h2>
                                                                <div
                                                                    id={`faq-collapse-${index}-${faqIndex}`}
                                                                    className="accordion-collapse collapse"
                                                                    aria-labelledby={`faq-heading-${index}-${faqIndex}`}
                                                                    data-bs-parent={`#faq-accordion-${index}`}
                                                                >
                                                                    <div className="accordion-body" style={{ color: "#666", fontSize: "0.9rem", padding: "12px 16px" }}>
                                                                        {faq.answer}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    {section.generalHeader && (
                                                        <h5 className="mb-3" style={{ fontWeight: "600", fontSize: "1.1rem" }}>
                                                            {section.generalHeader}
                                                        </h5>
                                                    )}
                                                    {section.generalContent && (
                                                        section.generalAsBullets ? (
                                                            <ul style={{ lineHeight: "1.7", color: "#666", fontSize: "0.95rem", paddingLeft: "20px" }}>
                                                                {section.generalContent.split('\n').filter(line => line.trim()).map((line, i) => (
                                                                    <li key={i} className="mb-2">{line.trim()}</li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <div style={{ lineHeight: "1.7", color: "#666", fontSize: "0.95rem", whiteSpace: "pre-wrap" }}>
                                                                {section.generalContent}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    ))
                )}
            </div>
        </div>
    );
}
